import React, { useEffect, useMemo, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Button } from "primereact/button";
import CustomProgressBar from "../../components/CustomProgressBar";
import { API_GET_FILE_URL, API_UPLOAD_FILE } from "../../api/media.service";
import { CustomButton } from "../../components/CustomButton";

export default function CreateTicketModal({
  onSubmit,
  submitting = false,
  success = false,
  error = null,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    description: "",
  });

  const [errors, setErrors] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const uploadQueueRef = useRef([]);
  const attachmentsRef = useRef([]);
  const isProcessingRef = useRef(false);
  const fileUploadRef = useRef(null);

  const updateAttachments = (updater) => {
    setAttachments((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      attachmentsRef.current = next;
      return next;
    });
  };

  const visibleAttachments = useMemo(
    () => attachments.filter((item) => item.status !== "removed"),
    [attachments],
  );

  const uploadedAttachments = useMemo(
    () =>
      visibleAttachments.filter(
        (item) => item.status === "uploaded" && item.url,
      ),
    [visibleAttachments],
  );

  const aggregateProgress = useMemo(() => {
    if (!visibleAttachments.length) return 0;
    const total = visibleAttachments.reduce(
      (acc, item) => acc + (item.progress || 0),
      0,
    );
    return Math.round(total / visibleAttachments.length);
  }, [visibleAttachments]);

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      subject: "",
      description: "",
    });
    setErrors({});
    updateAttachments([]);
    uploadQueueRef.current = [];
    isProcessingRef.current = false;
    setIsUploading(false);
    fileUploadRef.current?.clear();
  };

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    if (success) {
      resetForm();
    }
  }, [success]);

  const processUploadQueue = async () => {
    if (isProcessingRef.current) return;
    if (!uploadQueueRef.current.length) return;

    isProcessingRef.current = true;
    setIsUploading(true);

    while (uploadQueueRef.current.length) {
      const nextId = uploadQueueRef.current.shift();
      const currentItem = attachmentsRef.current.find(
        (item) => item.id === nextId,
      );

      if (!currentItem || currentItem.status === "removed") continue;

      updateAttachments((prev) =>
        prev.map((item) =>
          item.id === nextId
            ? {
                ...item,
                status: "uploading",
                progress: Math.max(item.progress || 0, 35),
                error: null,
              }
            : item,
        ),
      );

      try {
        const resp = await API_GET_FILE_URL();
        const uploadSrc = resp?.data?.src;
        const uploadUrl = resp?.data?.url;

        await API_UPLOAD_FILE(
          uploadSrc,
          currentItem.file,
          currentItem.contentType,
        );

        const latest = attachmentsRef.current.find(
          (item) => item.id === nextId,
        );
        if (!latest || latest.status === "removed") continue;

        updateAttachments((prev) =>
          prev.map((item) =>
            item.id === nextId
              ? {
                  ...item,
                  status: "uploaded",
                  progress: 100,
                  url: uploadUrl || item.url || null,
                  error: null,
                }
              : item,
          ),
        );
      } catch (err) {
        const latest = attachmentsRef.current.find(
          (item) => item.id === nextId,
        );
        if (!latest || latest.status === "removed") continue;

        updateAttachments((prev) =>
          prev.map((item) =>
            item.id === nextId
              ? {
                  ...item,
                  status: "failed",
                  progress: 0,
                  error: "Upload failed. Retry or delete this file.",
                }
              : item,
          ),
        );
      }
    }

    isProcessingRef.current = false;
    setIsUploading(false);
  };

  const enqueueUploadIds = (ids) => {
    if (!ids.length) return;
    uploadQueueRef.current = [...uploadQueueRef.current, ...ids];
    processUploadQueue();
  };

  const handleFileSelect = (e) => {
    const files = e.files || [];
    if (!files.length) return;

    const newItems = files.map((file, index) => ({
      id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      name: file.name,
      size: file.size || 0,
      contentType: file.type || "application/octet-stream",
      status: "queued",
      progress: 0,
      url: null,
      error: null,
    }));

    updateAttachments((prev) => [...prev, ...newItems]);
    enqueueUploadIds(newItems.map((item) => item.id));
    fileUploadRef.current?.clear();
  };

  const handleRemoveAttachment = (id) => {
    updateAttachments((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: "removed", error: null } : item,
      ),
    );
    uploadQueueRef.current = uploadQueueRef.current.filter(
      (queueId) => queueId !== id,
    );
    fileUploadRef.current?.clear();
  };

  const handleRemoveAll = () => {
    updateAttachments((prev) =>
      prev.map((item) => ({
        ...item,
        status: "removed",
        error: null,
        progress: 0,
      })),
    );
    uploadQueueRef.current = [];
    fileUploadRef.current?.clear();
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Full name is required.";
    if (!form.email.trim()) nextErrors.email = "Email is required.";
    if (
      form.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
    ) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!form.subject.trim()) nextErrors.subject = "Subject is required.";
    if (!form.description.trim())
      nextErrors.description = "Description is required.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  return (
    <div className="p-fluid">
      <div
        className="border-1 border-200 border-round-xl overflow-hidden"
        style={{ background: "#f8fafc" }}
      >
        <div className="px-4 py-3 border-bottom-1 border-200">
          <div className="text-2xl font-semibold text-900">
            Create New Ticket
          </div>
          <div className="text-600 mt-1">
            Share your issue and attach files if needed.
          </div>
        </div>

        <div className="grid px-4 py-4">
          {success && (
            <div className="col-12">
              <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-semibold">
                Ticket created successfully.
              </div>
            </div>
          )}

          <div className="col-12 md:col-6">
            <label className="font-medium mb-2 block">
              Full Name <span className="text-red-600">*</span>
            </label>
            <InputText
              value={form.name}
              placeholder="John Doe"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={errors.name ? "p-invalid" : ""}
            />
            {errors.name && (
              <small className="text-red-600 mt-1 block">{errors.name}</small>
            )}
          </div>

          <div className="col-12 md:col-6">
            <label className="font-medium mb-2 block">
              Email <span className="text-red-600">*</span>
            </label>
            <InputText
              value={form.email}
              placeholder="john@example.com"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={errors.email ? "p-invalid" : ""}
            />
            {errors.email && (
              <small className="text-red-600 mt-1 block">{errors.email}</small>
            )}
          </div>

          <div className="col-12">
            <label className="font-medium mb-2 block">
              Subject <span className="text-red-600">*</span>
            </label>
            <InputText
              value={form.subject}
              placeholder="Short summary of the issue"
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={errors.subject ? "p-invalid" : ""}
            />
            {errors.subject && (
              <small className="text-red-600 mt-1 block">
                {errors.subject}
              </small>
            )}
          </div>

          <div className="col-12">
            <label className="font-medium mb-2 block">
              Description <span className="text-red-600">*</span>
            </label>
            <InputTextarea
              rows={4}
              autoResize
              placeholder="Describe the issue in detail..."
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className={errors.description ? "p-invalid" : ""}
            />
            {errors.description && (
              <small className="text-red-600 mt-1 block">
                {errors.description}
              </small>
            )}
          </div>

          <div className="col-12">
            <div className="flex align-items-center justify-content-between mb-2">
              <label className="font-medium block">
                Attachments (optional)
              </label>
              {visibleAttachments.length > 0 && (
                <Button
                  type="button"
                  label="Remove All"
                  className="p-button-text p-button-sm"
                  onClick={handleRemoveAll}
                />
              )}
            </div>

            <div className="grid">
              <div className="col-12 lg:col-8">
                <div
                  className="border-1 border-dashed border-300 border-round-lg p-4 h-full"
                  style={{ background: "#eef2ff" }}
                >
                  <div className="text-center">
                    <div className="text-xl font-semibold text-900">
                      Drag and drop files here
                    </div>
                    <div className="text-600 mt-2">or click to browse</div>
                    <div className="text-500 text-sm mt-2">
                      Max 10 files, 25MB each
                    </div>
                    <div className="mt-3">
                      <FileUpload
                        ref={fileUploadRef}
                        name="files[]"
                        mode="basic"
                        auto={false}
                        chooseLabel="Browse Files"
                        customUpload
                        multiple
                        onSelect={handleFileSelect}
                        accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 lg:col-4">
                <div className="surface-card border-1 border-200 border-round-lg p-3 h-full">
                  <div className="text-900 font-semibold mb-3">
                    Uploaded Files
                  </div>
                  {uploadedAttachments.length === 0 && (
                    <div className="text-500 text-sm">
                      Uploaded files will appear here.
                    </div>
                  )}
                  {uploadedAttachments.map((item) => (
                    <div
                      key={item.id}
                      className="p-2 border-1 border-200 border-round mb-2"
                    >
                      <div className="flex justify-content-between align-items-start">
                        <div>
                          <div className="text-800 text-sm font-medium">
                            {item.name}
                          </div>
                          <div className="text-600 text-xs mt-1">
                            Upload complete
                          </div>
                        </div>
                        <Button
                          type="button"
                          icon="pi pi-trash"
                          className="p-button-text p-button-sm"
                          onClick={() => handleRemoveAttachment(item.id)}
                        />
                      </div>
                      <div
                        className="w-full mt-2 border-round"
                        style={{ height: "5px", background: "#e2e8f0" }}
                      >
                        <div
                          className="border-round"
                          style={{
                            height: "5px",
                            width: "100%",
                            background: "#14b8a6",
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {isUploading && (
              <div className="mt-3">
                <CustomProgressBar progress={aggregateProgress} />
              </div>
            )}
          </div>

          {error && (
            <div className="col-12">
              <small className="text-red-600">{error}</small>
            </div>
          )}

          <div className="">
            <div className="flex flex-wrap justify-content-between align-items-center gap-2 mt-1">
              {/* <CustomButton
                label="Clear"
                icon="pi pi-check"
                className="p-button-outlined p-button-sm"
                onClick={resetForm}
                disabled={submitting}
              /> */}
              <CustomButton
                label={submitting ? "Submitting..." : "Submit Ticket"}
                icon="pi pi-check"
                className="p-button-sm"
                onClick={() => {
                  if (!validate()) return;
                  if (isUploading) return;
                  const attachmentUrls = attachmentsRef.current
                    .filter((item) => item.status === "uploaded" && item.url)
                    .map((item) => item.url);
                  onSubmit({ ...form, attachmentUrls });
                }}
                disabled={submitting || isUploading}
                // style={{ minWidth: "104px", height: "24px", fontSize: "0.72rem" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
